'use client';

import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import { useActionState, useEffect, useRef } from 'react';

import { authenticate } from '@/lib/auth/actions';
import { twMerge } from '@/tailwind-merge.config';

interface LoginFormProps {
  callbackUrl?: string;
  disableRedirect?: boolean;
}

export function LoginForm({ callbackUrl, disableRedirect = false }: LoginFormProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { locale } = useParams();
  const { update } = useSession();
  const hasHandledSuccess = useRef(false);
  const [errorMessage, formAction, isPending] = useActionState(
    authenticate,
    undefined
  );

  useEffect(() => {
    if (errorMessage === 'success' && !hasHandledSuccess.current) {
      hasHandledSuccess.current = true;

      if (disableRedirect) {
        update();
        router.refresh();
      } else {
        update().then((updatedSession) => {
          const hasRoles =
            updatedSession?.user?.roles && updatedSession.user.roles.length > 0;
          const defaultUrl = hasRoles ? `/${locale}/neuigkeiten` : `/${locale}`;
          router.push(callbackUrl || defaultUrl);
          router.refresh();
        });
      }
    }
  }, [
    errorMessage,
    router,
    isPending,
    callbackUrl,
    disableRedirect,
    update,
    locale,
  ]);

  return (
    <div className="relative min-h-80 w-full bg-palmberg-grau">
      {/* Desktop: Absolute positioned layout */}
      <div className="relative grid grid-cols-1 desktop:grid-cols-2">
        {/* Title Desktop - Left side, vertically centered */}
        <div className="px-8 pt-14 tablet:pl-28 tablet:pt-20">
          <h1 className="text-h1Mobile uppercase tablet:text-h1Desktop">
            {t('auth.fachwelt')}
            <br />
            {t('auth.login')}
          </h1>
        </div>

        {/* Form Desktop - Right side */}
        <div className="px-8 pt-10 tablet:pl-28">
          <form action={formAction} className="tablet:w-[527px]">
            {/* Username field */}
            <div className="mb-3">
              <label
                htmlFor="username-desktop"
                className="mb-2 block text-sm font-normal uppercase leading-normal"
              >
                {t('auth.login-id')}
              </label>
              <input
                id="username-desktop"
                name="username"
                type="text"
                autoComplete="username"
                required
                disabled={isPending}
                placeholder=""
                className={twMerge(
                  'block h-[40px] w-full border border-black bg-white px-4 py-2 text-p1CopyDesktop font-light',
                  'placeholder:text-black',
                  'focus:border-black focus:outline-none focus:ring-0',
                  'disabled:cursor-not-allowed disabled:bg-gray-100'
                )}
              />
            </div>

            {/* Password field */}
            <div className="mb-[29px]">
              <label
                htmlFor="password-desktop"
                className="mb-2 block text-sm font-normal uppercase leading-normal tracking-[0.7px]"
              >
                {t('auth.password')}
              </label>
              <input
                id="password-desktop"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                disabled={isPending}
                placeholder=""
                className={twMerge(
                  'block h-[40px] w-full border border-black bg-white px-4 py-2 text-p1CopyDesktop font-light',
                  'placeholder:text-black',
                  'focus:border-black focus:outline-none focus:ring-0',
                  'disabled:cursor-not-allowed disabled:bg-gray-100'
                )}
              />
            </div>

            {/* Error message */}
            {errorMessage && errorMessage !== 'success' && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">
                  {t(`auth.errors.${errorMessage}`)}
                </p>
              </div>
            )}

            <div className="tablet:flex tablet:justify-between">
              {/* Links */}
              <div className="mb-[6px] space-y-[2.13px] text-p1CopyDesktop font-light">
                <Link
                  href="/user/forgot-password"
                  className="block underline decoration-solid hover:text-palmberg-dunkel-grn"
                >
                  {t('auth.forgot-password')}
                </Link>
                <Link
                  href="/user/register"
                  className="block underline decoration-solid hover:text-palmberg-dunkel-grn"
                >
                  {t('auth.register-now')}
                </Link>
              </div>

              {/* Button - Positioned to the right */}
              <div className="py-4 tablet:py-0">
                <button
                  type="submit"
                  disabled={isPending}
                  className={twMerge(
                    'btn btn--primary btn--icon btn--icon--arrow-left'
                  )}
                >
                  {isPending ? t('auth.signing-in') : t('auth.sign-in')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
